import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../styles/Navbar.css';

const Navbar = () => {
  const [mobileOpen, setMobileOpen]         = useState(false);
  const [scrolled, setScrolled]             = useState(false);
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const navMenuRef = useRef(null);
  const linkRefs   = useRef({});
  const location   = useLocation();

  const navItems = [
    { label: 'Home',     path: '/',        icon: '⌂' },
    { label: 'About',    path: '/about',   icon: '◎' },
    { label: 'Services', path: '/services',icon: '◈' },
    { label: 'Career',   path: '/career',  icon: '🧩' },
    { label: 'Partners', path: '/partners',icon: '🤝' },
    { label: 'Contact',  path: '/contact', icon: '✉' },
  ];

  /* ── scroll watcher ── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ── sliding active indicator ── */
  useEffect(() => {
    const match = navItems.find(n => n.path === location.pathname);
    if (!match) return;
    const el  = linkRefs.current[match.path];
    const nav = navMenuRef.current;
    if (!el || !nav) return;
    const navRect = nav.getBoundingClientRect();
    const elRect  = el.getBoundingClientRect();
    setIndicatorStyle({ left: elRect.left - navRect.left, width: elRect.width });
  }, [location.pathname]);

  /* ── close drawer on route change ── */
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  /* ── lock body scroll when drawer open ── */
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <>
      <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}>
        <div className="navbar-container">

          {/* ── Hamburger (left, mobile) ── */}
          <button
            className={`hamburger ${mobileOpen ? 'hamburger--open' : ''}`}
            onClick={() => setMobileOpen(o => !o)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            <span className="ham-line ham-line-1" />
            <span className="ham-line ham-line-2" />
            <span className="ham-line ham-line-3" />
          </button>

          {/* ── Logo ── */}
          <Link to="/" className="navbar-logo" aria-label="Incogniflex home">
            <div className="logo-img-wrap">
              <img
                src="https://res.cloudinary.com/dwdkccvnz/image/upload/v1772120373/logo_m30til.png"
                alt="Incogniflex"
                className="navbar-logo-img"
              />
            </div>
          </Link>

          {/* ── Desktop nav ── */}
          <ul className="navbar-menu" ref={navMenuRef} role="list">
            <li className="nav-indicator" style={indicatorStyle} aria-hidden />
            {navItems.map((item, i) => (
              <li key={item.path} className="navbar-item" style={{ '--i': i }}>
                <Link
                  to={item.path}
                  ref={el => (linkRefs.current[item.path] = el)}
                  className={`navbar-link ${isActive(item.path) ? 'active' : ''}`}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>

          {/* ── CTA ── */}
          <div className="navbar-cta-wrap">
            <a href="mailto:aiprosevice@gmail.com" className="navbar-cta">
              <span>Get Started</span>
              <span className="cta-arrow">→</span>
            </a>
          </div>

        </div>
      </nav>

      {/* ══════════════ LEFT DRAWER ══════════════ */}
      {/* backdrop */}
      <div
        className={`drawer-backdrop ${mobileOpen ? 'drawer-backdrop--open' : ''}`}
        onClick={() => setMobileOpen(false)}
        aria-hidden
      />

      {/* drawer panel */}
      <aside
        className={`left-drawer ${mobileOpen ? 'left-drawer--open' : ''}`}
        aria-label="Navigation drawer"
        role="dialog"
        aria-modal="true"
      >
        {/* drawer header */}
        <div className="drawer-header">
          <Link to="/" className="drawer-logo" onClick={() => setMobileOpen(false)}>
            <img
              src="https://res.cloudinary.com/dwdkccvnz/image/upload/v1772120373/logo_m30til.png"
              alt="Incogniflex"
              className="drawer-logo-img"
            />
          </Link>
          <button
            className="drawer-close"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>

        {/* drawer links */}
        <nav className="drawer-nav" aria-label="Mobile navigation">
          <ul className="drawer-list" role="list">
            {navItems.map((item, i) => (
              <li
                key={item.path}
                className="drawer-item"
                style={{ '--di': i }}
              >
                <Link
                  to={item.path}
                  className={`drawer-link ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => setMobileOpen(false)}
                >
                  <span className="drawer-link-icon">{item.icon}</span>
                  <span className="drawer-link-label">{item.label}</span>
                  {isActive(item.path) && <span className="drawer-active-dot" />}
                  <span className="drawer-link-arrow">→</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* drawer footer */}
        <div className="drawer-footer">
          <a
            href="mailto:aiprosevice@gmail.com"
            className="drawer-cta"
            onClick={() => setMobileOpen(false)}
          >
            🚀 Get Started
          </a>
          <p className="drawer-tagline">Enterprise AI · Built for Scale</p>
          <p className="drawer-copy">© 2025 Incogniflex</p>
        </div>

        {/* decorative side glow */}
        <div className="drawer-glow" aria-hidden />
      </aside>
    </>
  );
};

export default Navbar;